import { useTaskPocketStore } from "./task-pocket-store";
/**
 * Zustand store for the board's tasks: every active project's, in one list.
 *
 * Boards filter it per frame (`boards`). Exposes a `triggerRefetch` action
 * that the WebSocket handler calls when a task.created activity arrives, so
 * the tasks tab re-fetches live.
 */

import { create } from "zustand";
import { mirroredStore } from "@/lib/mirrored-store";
import { listTasks } from "@/api/tasks";
import type {
  Task,
  TaskStatus,
  TaskSource,
  TaskDependency,
  TaskComment,
} from "@/api/tasks";
import { captureDesktopChatAuthScopeGuard } from "@/desktop/desktop-chat-auth-scope";
import { translate } from "@/i18n";
import { storesNoticesMessages } from "@/i18n/messages/stores-notices";
import {
  withBoardDefaults,
  withPickedProjects,
  withPickedView,
  withResetBoard,
  type BoardProjects,
  type Boards,
  type TaskViewMode,
} from "./task-view-session";

interface TasksState {
  tasks: Task[];
  /** Local-only comments for synthesized tasks that should not hit REST. */
  localTaskComments: Record<string, TaskComment[]>;
  isLoading: boolean;
  /** The last fetch settled, loaded or failed: the wait is over. */
  loaded: boolean;
  /** The last fetch left out older open tasks. */
  truncated: boolean;
  /** Increments each time a refetch should be triggered (WS push). */
  refetchSignal: number;

  /** Active search query for filtering tasks by title. */
  searchQuery: string;
  /** Active status filter; "all" means no filter. */
  filterStatus: TaskStatus | "all";
  /** Active source filter; "all" means no filter. */
  filterSource: TaskSource | "all";

  /** Filter tasks by assigned agent ID; null means show all. */
  filterAgent: string | null;
  /** Whether completed tasks are visible in list/calendar. */
  showCompleted: boolean;
  /** Current month for calendar navigation. */
  calendarMonth: Date;
  /** Each board frame's layout and project filter, by frame id. */
  boards: Boards;
  /** Sort field for list/calendar views. */
  sortBy: "priority" | "due_date" | "created" | "status" | "agent";
  /** Sort direction. */
  sortDirection: "asc" | "desc";
  /** Filter by priority; "all" means no filter. */
  filterPriority: "all" | "urgent" | "high" | "medium" | "low";
  /** Filter by label ID; null means no filter. */
  filterLabel: string | null;
  /** Filter by created-date range; "all" means no filter. */
  filterCreatedRange: "all" | "today" | "this_week" | "this_month";

  /** Fetch every active project's tasks and store the result. */
  fetchTasks: () => Promise<void>;

  /** Called by WS handler when task.created activity arrives. */
  triggerRefetch: () => void;

  /** Update a single task's status in-place from WS push. */
  updateTaskStatus: (taskId: string, status: TaskStatus) => void;

  /**
   * Change a task's status via API and update local state.
   * Returns true on success, false when the transition was rejected
   * (state is refetched to snap the board back).
   */
  changeTaskStatus: (
    projectId: string,
    taskId: string,
    status: TaskStatus,
  ) => Promise<boolean>;

  /** Edit a task's fields via API and update local state. */
  editTask: (
    projectId: string,
    taskId: string,
    fields: { title?: string; description?: string; priority?: string },
  ) => Promise<void>;

  /** Set the agent filter. */
  setFilterAgent: (agentId: string | null) => void;

  /** Prepend a new task from WS push. */
  handleTaskCreated: (task: Task) => void;
  /** Update a task's blocked_by_detail and status to 'blocked' from WS push. */
  handleTaskBlocked: (taskId: string, blockedBy: TaskDependency[]) => void;
  /** Clear a task's blocked_by_detail and set status to 'todo' from WS push. */
  handleTaskUnblocked: (taskId: string) => void;
  /** Replace a task in-place from WS push (field changes). */
  handleTaskUpdated: (task: Task & { replaces_task_id?: string }) => void;
  /** Set comments for a local-only synthesized task. */
  setLocalTaskComments: (taskId: string, comments: TaskComment[]) => void;

  /** Remove a task from the store (optimistic delete). */
  removeTask: (taskId: string) => void;

  /** Set the search query filter. */
  setSearchQuery: (query: string) => void;
  /** Set the status filter. */
  setFilterStatus: (status: TaskStatus | "all") => void;
  /** Set the source filter. */
  setFilterSource: (source: TaskSource | "all") => void;
  /** Toggle completed task visibility. */
  setShowCompleted: (show: boolean) => void;
  /** Set the calendar month. */
  setCalendarMonth: (month: Date) => void;
  /** A board's defaults, applied while the user has not picked on it. */
  applyBoardDefaults: (
    frameId: string,
    projectId: string,
    defaultView: TaskViewMode,
    defaultProjects: BoardProjects,
  ) => void;
  /** A board's layout; the pick wins until a project switch. */
  setBoardView: (frameId: string, view: TaskViewMode) => void;
  /** A board's project filter; the pick wins until a project switch. */
  setBoardProjects: (frameId: string, projects: BoardProjects) => void;
  /** Forget every board's picks (a project switch). */
  clearBoards: () => void;
  /** Set the sort field. */
  setSortBy: (field: TasksState["sortBy"]) => void;
  /** Set the sort direction. */
  setSortDirection: (dir: "asc" | "desc") => void;
  /** Set the priority filter. */
  setFilterPriority: (priority: TasksState["filterPriority"]) => void;
  /** Set the label filter. */
  setFilterLabel: (labelId: string | null) => void;
  /** Set the created-date range filter. */
  setFilterCreatedRange: (range: TasksState["filterCreatedRange"]) => void;
  /** Reset all display settings, and this board's layout and filter, to defaults. */
  resetDisplay: (frameId: string) => void;

  /** Clear tasks (e.g. on project change). */
  clear: () => void;
}

export function normalizeCalendarMonth(value: unknown): Date {
  const date =
    value instanceof Date
      ? value
      : typeof value === "string" || typeof value === "number"
        ? new Date(value)
        : new Date();
  return Number.isNaN(date.getTime()) ? new Date() : date;
}

export function normalizeTasksPatch<T extends Record<string, unknown>>(
  patch: T,
): T {
  if (!Object.prototype.hasOwnProperty.call(patch, "calendarMonth"))
    return patch;
  return {
    ...patch,
    calendarMonth: normalizeCalendarMonth(patch.calendarMonth),
  };
}

function mergeFetchedTasksWithLocalTasks(
  fetchedTasks: Task[],
  currentTasks: Task[],
  localTaskComments: Record<string, TaskComment[]>,
): Task[] {
  const fetchedIds = new Set(fetchedTasks.map((task) => task.id));
  const localTasks = currentTasks.filter(
    (task) =>
      Object.prototype.hasOwnProperty.call(localTaskComments, task.id) &&
      !fetchedIds.has(task.id),
  );
  return [...localTasks, ...fetchedTasks];
}

export const useTasksStore = create<TasksState>()(
  mirroredStore("tasks", (set, get) => ({
    tasks: [],
    localTaskComments: {},
    isLoading: false,
    loaded: false,
    truncated: false,
    refetchSignal: 0,
    searchQuery: "",
    filterStatus: "all",
    filterSource: "all",
    filterAgent: null,
    showCompleted: false,
    calendarMonth: new Date(),
    boards: {},
    sortBy: "priority",
    sortDirection: "desc",
    filterPriority: "all",
    filterLabel: null,
    filterCreatedRange: "all",

    fetchTasks: async () => {
      const isCurrentAuthScope = captureDesktopChatAuthScopeGuard();
      set({ isLoading: true });
      try {
        const { tasks: fetchedTasks, truncated } = await listTasks();
        if (!isCurrentAuthScope()) return;
        set((state) => ({
          tasks: mergeFetchedTasksWithLocalTasks(
            fetchedTasks,
            state.tasks,
            state.localTaskComments,
          ),
          isLoading: false,
          loaded: true,
          truncated,
        }));
      } catch {
        if (!isCurrentAuthScope()) return;
        set({ isLoading: false, loaded: true });
      }
    },

    triggerRefetch: () =>
      set((state) => ({ refetchSignal: state.refetchSignal + 1 })),

    updateTaskStatus: (taskId, status) =>
      set((state) => ({
        tasks: state.tasks.map((t) => (t.id === taskId ? { ...t, status } : t)),
      })),

    changeTaskStatus: async (projectId, taskId, newStatus) => {
      if (
        Object.prototype.hasOwnProperty.call(get().localTaskComments, taskId)
      ) {
        set((state) => ({
          tasks: state.tasks.map((task) =>
            task.id === taskId
              ? {
                  ...task,
                  status: newStatus,
                  updated_at: new Date().toISOString(),
                }
              : task,
          ),
        }));
        return true;
      }
      try {
        const { updateTaskStatus: apiUpdateStatus } =
          await import("@/api/tasks");
        const updated = await apiUpdateStatus(projectId, taskId, newStatus);
        set((state) => ({
          tasks: state.tasks.map((t) => (t.id === taskId ? updated : t)),
        }));
        return true;
      } catch (error) {
        // Surface the server reason (e.g. parent with open subtasks) — then
        // refetch to sync state (rejected transition snaps back).
        const message =
          error instanceof Error && error.message
            ? error.message
            : translate(storesNoticesMessages, "statusChangeFailed");
        try {
          const { useNotificationStore } = await import("./notification-store");
          useNotificationStore.getState().addSystemToast({
            id: `task-status-error-${taskId}`,
            title: message,
            level: "error",
          });
        } catch {
          // Toast is best-effort; the refetch below still syncs state.
        }
        await get().fetchTasks();
        return false;
      }
    },

    editTask: async (projectId, taskId, fields) => {
      try {
        const { updateTask: apiUpdate } = await import("@/api/tasks");
        const updated = await apiUpdate(projectId, taskId, fields);
        set((state) => ({
          tasks: state.tasks.map((t) => (t.id === taskId ? updated : t)),
        }));
      } catch {
        await get().fetchTasks();
      }
    },

    setFilterAgent: (agentId) => set({ filterAgent: agentId }),

    handleTaskCreated: (task) =>
      set((state) => {
        // Dedup: skip if a task with this id is already in the store.
        // Prevents duplicates when a WS task.created event arrives for a
        // task that was already loaded via fetchTasks (REST + WS race).
        if (state.tasks.some((t) => t.id === task.id)) return state;
        return { tasks: [task, ...state.tasks] };
      }),

    handleTaskBlocked: (taskId, blockedBy) =>
      set((state) => ({
        tasks: state.tasks.map((t) =>
          t.id === taskId
            ? { ...t, status: "blocked" as const, blocked_by_detail: blockedBy }
            : t,
        ),
      })),

    handleTaskUnblocked: (taskId) =>
      set((state) => ({
        tasks: state.tasks.map((t) =>
          t.id === taskId
            ? { ...t, status: "todo" as const, blocked_by_detail: [] }
            : t,
        ),
      })),

    handleTaskUpdated: (task) => {
      const pocket = useTaskPocketStore.getState();
      if (
        task.replaces_task_id &&
        task.replaces_task_id !== task.id &&
        pocket.taskId === task.replaces_task_id &&
        pocket.anchor
      ) {
        pocket.open(task.id, pocket.anchor);
      }
      set((state) => {
        const replaced =
          task.replaces_task_id !== task.id ? task.replaces_task_id : undefined;
        const tasks = state.tasks.filter((item) => item.id !== replaced);
        return {
          tasks: tasks.some((item) => item.id === task.id)
            ? tasks.map((item) => (item.id === task.id ? task : item))
            : replaced
              ? [task, ...tasks]
              : tasks,
        };
      });
    },

    setLocalTaskComments: (taskId, comments) =>
      set((state) => ({
        localTaskComments: {
          ...state.localTaskComments,
          [taskId]: comments,
        },
      })),

    removeTask: (taskId) => {
      if (useTaskPocketStore.getState().taskId === taskId)
        useTaskPocketStore.getState().close();
      set((state) => ({
        tasks: state.tasks.filter((t) => t.id !== taskId),
        localTaskComments: Object.fromEntries(
          Object.entries(state.localTaskComments).filter(
            ([id]) => id !== taskId,
          ),
        ),
      }));
    },

    setSearchQuery: (query) => set({ searchQuery: query }),
    setFilterStatus: (status) => set({ filterStatus: status }),
    setFilterSource: (source) => set({ filterSource: source }),
    setShowCompleted: (show) => set({ showCompleted: show }),
    setCalendarMonth: (month) =>
      set({ calendarMonth: normalizeCalendarMonth(month) }),
    applyBoardDefaults: (frameId, projectId, defaultView, defaultProjects) =>
      set((state) => {
        const boards = withBoardDefaults(
          state.boards,
          frameId,
          projectId,
          defaultView,
          defaultProjects,
        );
        return boards ? { boards } : state;
      }),
    setBoardView: (frameId, view) =>
      set((state) => ({ boards: withPickedView(state.boards, frameId, view) })),
    setBoardProjects: (frameId, projects) =>
      set((state) => ({
        boards: withPickedProjects(state.boards, frameId, projects),
      })),
    clearBoards: () => set({ boards: {} }),
    setSortBy: (field) => set({ sortBy: field }),
    setSortDirection: (dir) => set({ sortDirection: dir }),
    setFilterPriority: (priority) => set({ filterPriority: priority }),
    setFilterLabel: (labelId) => set({ filterLabel: labelId }),
    setFilterCreatedRange: (range) => set({ filterCreatedRange: range }),
    resetDisplay: (frameId) =>
      set((state) => ({
        sortBy: "priority",
        sortDirection: "desc",
        filterStatus: "all",
        filterPriority: "all",
        filterAgent: null,
        filterSource: "all",
        filterLabel: null,
        filterCreatedRange: "all",
        boards: withResetBoard(state.boards, frameId),
        showCompleted: false,
      })),

    clear: () =>
      set({
        tasks: [],
        localTaskComments: {},
        isLoading: false,
        loaded: false,
        truncated: false,
        searchQuery: "",
        filterStatus: "all",
        filterSource: "all",
        filterAgent: null,
        showCompleted: false,
        calendarMonth: new Date(),
        boards: {},
        sortBy: "priority",
        sortDirection: "desc",
        filterPriority: "all",
        filterLabel: null,
        filterCreatedRange: "all",
      }),
  })),
);

/**
 * Check whether a task's `created_at` timestamp falls within the given
 * preset range. Boundaries are computed in the user's local time (the
 * user is in Berlin). Week start is Monday (European convention).
 */
export function isWithinCreatedRange(
  createdAt: string,
  range: TasksState["filterCreatedRange"],
): boolean {
  if (range === "all") return true;
  const created = new Date(createdAt);
  if (Number.isNaN(created.getTime())) return true;
  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  );
  if (range === "today") return created >= startOfToday;
  if (range === "this_week") {
    const day = startOfToday.getDay(); // 0=Sun
    const monDelta = day === 0 ? 6 : day - 1;
    const startOfWeek = new Date(startOfToday);
    startOfWeek.setDate(startOfToday.getDate() - monDelta);
    return created >= startOfWeek;
  }
  if (range === "this_month") {
    return created >= new Date(now.getFullYear(), now.getMonth(), 1);
  }
  return true;
}
